import {
  Component,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { map, catchError, take } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import { Router } from '@angular/router';
import { MatIcon } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SocialPlatformI,
  ContentSiteI,
} from "../../../interfaces/core.interface";
import { CoreService } from "../../../services/core.service";
import { SocialAiFollowerGrowthChartComponent } from "../social-ai-follower-growth-chart/social-ai-follower-growth-chart.component";
import { SocialAiTrendsChartComponent } from "../social-ai-trends-chart/social-ai-trends-chart.component";
import { SocialAiFormatBreakdownChartComponent } from "../social-ai-format-breakdown-chart/social-ai-format-breakdown-chart.component";
import { SocialAiAlertsChartComponent } from "../social-ai-alerts-chart/social-ai-alerts-chart.component";
import { SocialAiRetentionChartComponent } from "../social-ai-retention-chart/social-ai-retention-chart.component";
import { SocialAiHeatmapChartComponent } from "../social-ai-heatmap-chart/social-ai-heatmap-chart.component";
import { SocialAiAlertsTabComponent } from "../../../pages/social-ai-alerts-tab/social-ai-alerts-tab.component";
import {
  NgApexchartsModule,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexGrid,
  ApexYAxis,
  ApexTooltip,
  ApexTitleSubtitle,
  ApexLegend,
} from "ng-apexcharts";
import { ActivatedRoute } from '@angular/router';

interface MetricValue {
  value: number | null;
  diff: number | null;
}

export type ChartOptions = Partial<{
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
}>;

@Component({
  selector: "app-social-ai-chart",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgApexchartsModule,
    SocialAiFollowerGrowthChartComponent,
    SocialAiTrendsChartComponent,
    SocialAiFormatBreakdownChartComponent,
    SocialAiAlertsChartComponent,
    SocialAiRetentionChartComponent,
    SocialAiHeatmapChartComponent,
    SocialAiAlertsTabComponent,
    MatIcon,
    MatTooltipModule
  ],
  templateUrl: "./social-ai-chart.component.html",
  styleUrl: "./social-ai-chart.component.scss",
})
export class SocialAiChartComponent implements AfterViewInit {
  public chartOptions6: ChartOptions = this.createChartOptions([], []);
  activeView: string = "followers";
  isLoaded: boolean = false;
  isMetricsLoaded = false;
  pages: any[] = [];
  sites: ContentSiteI[] = [];
  platforms: SocialPlatformI[] = [];
  filteredPlatforms: SocialPlatformI[] = [];
  selectedSiteId: number | null = null;
  selectedPlatformId: number | null = null;
  selectedPeriod: string = "days_28";
  companyID: number | null = null;
  lastInstaMeta: any = null;
  alertsSubView: 'page' | 'table' = 'page';
  selectedFormat: string | null = null;
  selectedAlertType: string | null = null;

  facebookFormats = [
    { value: "photolink", label: "Facebook Link Post" },
    { value: "fbphotopost", label: "Facebook Photo Post" },
    { value: "fbfirstcomment", label: "Facebook Photo Post (first comment)" },
    { value: "fbvideo", label: "Facebook Video Post" },
    { value: "fbstoryvideo", label: "Facebook Story Video" },
    { value: "fbstoryphoto", label: "Facebook Story Photo" },
    { value: "fbreel", label: "Facebook Reel Post" },
    { value: "fbstoryreel", label: "Facebook Story Reel" },
  ];

  instagramFormats = [
    { value: "igphotopost", label: "Instagram Photo Post" },
    { value: "igvideo", label: "Instagram Video Post" },
    { value: "igcarousel", label: "Instagram Carousel Post" },
    { value: "igstoryvideo", label: "Instagram Story Video" },
    { value: "igstoryphoto", label: "Instagram Story Photo" },
    { value: "igreel", label: "Instagram Reel Post" },
    { value: "igstoryreel", label: "Instagram Story Reel" },
  ];

  currentFormats: { value: string; label: string }[] = [];

  metrics: {
    reach: MetricValue;
    accountImpressions: MetricValue;
    profileImpressions: MetricValue;
  } = {
      reach: { value: null, diff: null },
      accountImpressions: { value: null, diff: null },
      profileImpressions: { value: null, diff: null },
    };

  lastChartValue: number | null = null;
  diffChartValue: number | null = null;

  constructor(
    private coreService: CoreService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  /** ===============================
   *  LIFECYCLE
   *  =============================== */
  ngOnInit() {
    const nav = this.router.getCurrentNavigation();
    const state = nav?.extras?.state as any;

    if (state) {
      this.selectedSiteId = state.siteId ?? this.selectedSiteId;
      this.selectedPlatformId = state.platformId ?? this.selectedPlatformId;
      this.selectedPeriod = state.period ?? this.selectedPeriod;
      this.selectedFormat = state.format ?? this.selectedFormat;
      this.alertsSubView = state.alertsView ?? this.alertsSubView;
    }

    this.route.paramMap.subscribe(params => {
      const view = params.get('view') || 'followers';

      if (this.activeView !== view) {
        this.activeView = view;
        this.reloadActiveView();
      }
    });
  }

  ngAfterViewInit(): void {
    this.companyID = this.coreService.user?.companyID || 0;

    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites = sites as ContentSiteI[];
      this.platforms = platforms as SocialPlatformI[];

      if (this.sites.length > 0) {
        this.selectedSiteId = this.sites[0].ID;
      }

      this.filteredPlatforms = this.platforms.filter(
        (p) =>
          p.site_id === this.selectedSiteId &&
          (p.platform.toLowerCase().includes("facebook") ||
            p.platform.toLowerCase().includes("instagram") ||
            p.platform.toLowerCase().includes("tiktok"))
      );
      
      // Add TikTok as a UI-only placeholder if not present for this site
      const hasTikTok = this.filteredPlatforms.some(p => p.platform.toLowerCase().includes("tiktok"));
      if (!hasTikTok && this.selectedSiteId) {
        // Create a placeholder TikTok platform
        const tiktokPlaceholder: SocialPlatformI = {
          id: -999, // Placeholder ID
          domain: "",
          platform: "TikTok",
          ms_contentsite_id: 0,
          social_media_id: 0,
          created_at: "",
          updated_at: "",
          access_token: "",
          companyID: this.companyID || 0,
          isApproved: "Approved",
          InitialPromptID: 0,
          site_id: this.selectedSiteId,
          app_id: "",
          app_secret: ""
        };
        this.filteredPlatforms.push(tiktokPlaceholder);
      }

      const fbPlatform = this.filteredPlatforms.find((p) =>
        p.platform.toLowerCase().includes("facebook")
      );
      this.selectedPlatformId = fbPlatform
        ? fbPlatform.id
        : this.filteredPlatforms[0]?.id || null;

      const selectedPlatform = fbPlatform || this.filteredPlatforms[0];
      if (selectedPlatform) {
        // For placeholder TikTok, use empty values to prevent API calls
        const isPlaceholderTikTok = selectedPlatform.id === -999;
        
        this.pages = [
          {
            social_media_id: isPlaceholderTikTok ? 0 : selectedPlatform.social_media_id,
            access_token: isPlaceholderTikTok ? "" : selectedPlatform.access_token,
            platform: selectedPlatform.platform,
          },
        ];
      }

      setTimeout(() => {
        this.loadFollowersChart();
        this.loadMetrics();
        this.updateFormatOptions();
      }, 200);
    });
  }

  /** ===============================
   *  EVENTS
   *  =============================== */

  resetFilters(): void {
    this.selectedPeriod = 'days_28';

    this.selectedFormat = null;
    this.selectedAlertType = null;
    this.alertsSubView = 'page';

    if (!this.sites.length || !this.platforms.length) {
      this.selectedSiteId = null;
      this.selectedPlatformId = null;
      this.filteredPlatforms = [];
      this.pages = [];
      return;
    }

    this.selectedSiteId = this.sites[0].ID;

    this.filteredPlatforms = this.platforms.filter(
      p =>
        p.site_id === this.selectedSiteId &&
        (p.platform.toLowerCase().includes('facebook') ||
          p.platform.toLowerCase().includes('instagram') ||
          p.platform.toLowerCase().includes('tiktok'))
    );
    
    // Add TikTok as a UI-only placeholder if not present for this site
    const hasTikTok = this.filteredPlatforms.some(p => p.platform.toLowerCase().includes("tiktok"));
    if (!hasTikTok && this.selectedSiteId) {
      // Create a placeholder TikTok platform
      const tiktokPlaceholder: SocialPlatformI = {
        id: -999, // Placeholder ID
        domain: "",
        platform: "TikTok",
        ms_contentsite_id: 0,
        social_media_id: 0,
        created_at: "",
        updated_at: "",
        access_token: "",
        companyID: this.companyID || 0,
        isApproved: "Approved",
        InitialPromptID: 0,
        site_id: this.selectedSiteId,
        app_id: "",
        app_secret: ""
      };
      this.filteredPlatforms.push(tiktokPlaceholder);
    }

    const defaultPlatform =
      this.filteredPlatforms.find(p =>
        p.platform.toLowerCase().includes('facebook')
      ) || this.filteredPlatforms[0];

    this.selectedPlatformId = defaultPlatform?.id || null;

    if (defaultPlatform) {
      // For placeholder TikTok, use empty values to prevent API calls
      const isPlaceholderTikTok = defaultPlatform.id === -999;
      
      this.pages = [{
        social_media_id: isPlaceholderTikTok ? 0 : defaultPlatform.social_media_id,
        access_token: isPlaceholderTikTok ? "" : defaultPlatform.access_token,
        platform: defaultPlatform.platform,
      }];
    } else {
      this.pages = [];
    }

    this.lastChartValue = null;
    this.diffChartValue = null;
  }

  reloadActiveView(): void {
    if (!this.selectedSiteId || !this.selectedPlatformId) return;

    switch (this.activeView) {
      case 'followers':
        this.loadFollowersChart();
        this.loadMetrics();
        break;

      case 'trends':
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;

      case 'heatmap':
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;

      case 'format':
      case 'alerts':
      case 'retention':
      default:
        this.cdr.detectChanges();
        break;
    }
  }

  updateFormatOptions() {
    const platform = this.filteredPlatforms.find(p => p.id === this.selectedPlatformId);
    if (!platform) return;

    const isInstagram = platform.platform.toLowerCase().includes("instagram");

    this.currentFormats = isInstagram ? this.instagramFormats : this.facebookFormats;
  }

  goToView(view: string): void {
    this.router.navigate(
      ['/dashboard', view],
      {
        state: {
          siteId: this.selectedSiteId,
          platformId: this.selectedPlatformId,
          period: this.selectedPeriod,
          format: this.selectedFormat,
          alertsView: this.alertsSubView
        }
      }
    );
  }

  private extractMetric(res: any) {
    const data = res.data?.[0]?.insight?.data?.[0]?.values || [];
    if (!data.length) return { value: null, diff: null };

    const first = data[0].value;
    const last = data.at(-1).value;
    return {
      value: last,
      diff: last - first,
    };
  }

  getRightCards() {
    const cards = {
      followers: [
        { title: "Format Breakdown", img: "assets/screens/format-chart.png", action: () => this.goToView("format") },
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.goToView("trends") },
        // { title: "Retention", img: "assets/screens/retention-chart.png", action: () => this.viewRetention("retention") },
        // {
        //   title: "Posting Heatmap",
        //   img: "assets/screens/heatmap-chart.png",
        //   action: () => this.viewHeatmap("heatmap"),
        // },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.goToView("alerts") },
      ],

      trends: [
        // { title: "Format Breakdown", img: "assets/screens/format-chart.png", action: () => this.viewFormat("format") },
        { title: "Posting Heatmap", img: "assets/screens/heatmap-chart.png", action: () => this.goToView("heatmap") },
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.goToView("followers") },
        // { title: "Retention", img: "assets/screens/retention-chart.png", action: () => this.viewRetention("retention") },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.goToView("alerts") },
      ],

      format: [
        // { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.viewFollowers("followers") },
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.goToView("trends") },
        //  { title: "Retention", img: "assets/screens/retention-chart.png", action: () => this.viewRetention("retention") },
        {
          title: "Posting Heatmap",
          img: "assets/screens/heatmap-chart.png",
          action: () => this.goToView("heatmap")
        },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.goToView("alerts") },
      ],

      alerts: [
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.goToView("followers") },
        { title: "Format Breakdown", img: "assets/screens/format-chart.png", action: () => this.goToView("format") },
        { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.goToView("trends") },
        // { title: "Retention", img: "assets/screens/retention-chart.png", action: () => this.viewRetention("retention") },
        // {
        //   title: "Posting Heatmap",
        //   img: "assets/screens/heatmap-chart.png",
        //   action: () => this.viewHeatmap("heatmap"),
        // },
      ],

      // retention: [
      //   { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.viewFollowers("followers") },
      //   { title: "Format Breakdown", img: "assets/screens/format-chart.png", action: () => this.viewFormat("format") },
      //   { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.viewTrends("trends") },

      //   {
      //     title: "Posting Heatmap",
      //     img: "assets/screens/heatmap-chart.png",
      //     action: () => this.viewHeatmap("heatmap"),
      //   },
      //   { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.viewAlerts("alerts") },
      // ],

      heatmap: [
        { title: "Overview", img: "assets/screens/overview-chart.png", action: () => this.goToView("followers") },
        { title: "Format Breakdown", img: "assets/screens/format-chart.png", action: () => this.goToView("format") },
        // { title: "Trends", img: "assets/screens/trends-chart.png", action: () => this.viewTrends("trends") },
        // { title: "Retention", img: "assets/screens/retention-chart.png", action: () => this.viewRetention("retention") },
        { title: "Alerts", img: "assets/screens/alerts.png", action: () => this.goToView("alerts") }
      ]
    };

    return (cards as Record<string, any[]>)[this.activeView] || [];
  }

  onAlertsTabChange(tab: 'page' | 'table') {
    this.alertsSubView = tab;
    this.selectedAlertType = null;
  }

  /** ===============================
 *  GETTERS
 *  =============================== */

  get showDropdowns(): boolean {
    return this.activeView === 'alerts' && this.alertsSubView === 'table';
  }

  get shouldShowTopDropdowns(): boolean {
    if (this.activeView !== 'alerts') return true;
    return this.alertsSubView === 'table';
  }

  get showPeriodDropdown(): boolean {
    return this.activeView !== 'alerts';
  }

  get isPostAlerts(): boolean {
    return this.activeView === 'alerts' && this.alertsSubView === 'table';
  }

  get isPageAlerts(): boolean {
    return this.activeView === 'alerts' && this.alertsSubView === 'page';
  }

  get alertTypeOptions() {
    return this.isPageAlerts
      ? this.pageAlertTypes
      : this.tableAlertTypes;
  }

  get isInstagram(): boolean {
    const platform = this.filteredPlatforms.find(
      p => p.id === this.selectedPlatformId
    );

    return !!platform?.platform?.toLowerCase().includes('instagram');
  }

  get currentViewTitle(): string {
    switch (this.activeView) {
      case "followers":
        return "Overview";
      case "trends":
        return "Trends";
      case "format":
        return "Format Breakdown";
      case "alerts":
        return "Alerts";
      case "retention":
        return "Retention";
      case "heatmap":
        return "Posting Heatmap";
      default:
        return "Overview";
    }
  }
  // =============================== //

  onSiteChange(siteId: number): void {
    this.selectedSiteId = siteId;

    this.filteredPlatforms = this.platforms.filter(
      (p) =>
        p.site_id === siteId &&
        (p.platform.toLowerCase().includes("facebook") ||
          p.platform.toLowerCase().includes("instagram") ||
          p.platform.toLowerCase().includes("tiktok"))
    );
    
    // Add TikTok as a UI-only placeholder if not present for this site
    const hasTikTok = this.filteredPlatforms.some(p => p.platform.toLowerCase().includes("tiktok"));
    if (!hasTikTok && siteId) {
      // Create a placeholder TikTok platform
      const tiktokPlaceholder: SocialPlatformI = {
        id: -999, // Placeholder ID
        domain: "",
        platform: "TikTok",
        ms_contentsite_id: 0,
        social_media_id: 0,
        created_at: "",
        updated_at: "",
        access_token: "",
        companyID: this.companyID || 0,
        isApproved: "Approved",
        InitialPromptID: 0,
        site_id: siteId,
        app_id: "",
        app_secret: ""
      };
      this.filteredPlatforms.push(tiktokPlaceholder);
    }

    this.lastChartValue = null;
    this.diffChartValue = null;

    if (!this.filteredPlatforms.length) {
      this.selectedPlatformId = null;
      this.pages = [];
      this.cdr.detectChanges();
      return;
    }

    const previousPlatform = this.platforms.find(
      (p) => p.id === this.selectedPlatformId
    );

    let platformToUse =
      (previousPlatform &&
        this.filteredPlatforms.find(
          (p) =>
            p.platform.toLowerCase() ===
            previousPlatform.platform.toLowerCase()
        )) ||
      this.filteredPlatforms.find((p) =>
        p.platform.toLowerCase().includes("facebook")
      ) ||
      this.filteredPlatforms[0];

    this.selectedPlatformId = platformToUse.id;

    // For placeholder TikTok, use empty values to prevent API calls
    const isPlaceholderTikTok = platformToUse.id === -999;
    
    this.pages = [
      {
        social_media_id: isPlaceholderTikTok ? 0 : platformToUse.social_media_id,
        access_token: isPlaceholderTikTok ? "" : platformToUse.access_token,
        platform: platformToUse.platform,
      },
    ];

    switch (this.activeView) {

      case "followers":
        this.loadFollowersChart();
        this.loadMetrics();
        break;

      case "trends":
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;

      case "heatmap":
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;

      case "format":
      case "alerts":
      case "retention":
      default:
        this.cdr.detectChanges();
        break;
    }
  }

  onPlatformChange(platformId: number): void {
    this.selectedPlatformId = platformId;

    const selectedPlatform = this.filteredPlatforms.find(
      (p) => p.id === platformId
    );
    if (!selectedPlatform) return;

    // For placeholder TikTok, use empty values to prevent API calls
    const isPlaceholderTikTok = selectedPlatform.id === -999;
    
    this.pages = [
      {
        social_media_id: isPlaceholderTikTok ? 0 : selectedPlatform.social_media_id,
        access_token: isPlaceholderTikTok ? "" : selectedPlatform.access_token,
        platform: selectedPlatform.platform,
      },
    ];

    switch (this.activeView) {
      case "followers":
        this.loadFollowersChart();
        this.loadMetrics();
        break;
      case "trends":
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;
      case "format":
      case "alerts":
      case "retention":
        this.cdr.detectChanges();
        break;
      case "heatmap":
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;
    }
    this.updateFormatOptions();
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;

    switch (this.activeView) {
      case "followers":
        this.loadFollowersChart();
        this.loadMetrics();
        break;
      case "trends":
        this.loadMetrics();
        this.fetchFollowersOnlyForMetricCards().then(r => {
          this.lastChartValue = r.value;
          this.diffChartValue = r.diff;
          this.cdr.detectChanges();
        });
        break;
      case "format":
      case "alerts":
      case "retention":
        this.cdr.detectChanges();
        break;
    }
  }

  /** ===============================
   *  LOADERS
   *  =============================== */

  private loadFollowersChart(platform?: any): void {
    if (!this.selectedSiteId || !this.selectedPlatformId) return;

    this.lastChartValue = null;
    this.diffChartValue = null;
    this.isLoaded = false;

    const selectedPlatform =
      platform ||
      this.filteredPlatforms.find((p) => p.id === this.selectedPlatformId) ||
      this.platforms.find((p) => p.id === this.selectedPlatformId);

    if (!selectedPlatform) {
      return;
    }

    const platformName = (selectedPlatform?.platform || "").toLowerCase();
    const isInstagram =
      platformName.includes("instagram") ||
      platformName.includes("insta") ||
      platformName.includes("ig");

    const periodToUse = isInstagram ? "day" : this.selectedPeriod;

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: periodToUse,
      pages: [
        {
          social_media_id: selectedPlatform.social_media_id,
          access_token: selectedPlatform.access_token,
        },
      ],
    };

    const endpoint = isInstagram
      ? `querymetrics/parseInstaAnalytics/${this.companyID}?metric=follower_count`
      : `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_follows`;

    this.coreService.postData(endpoint, payload).subscribe((res) => {
      this.isLoaded = true;

      const meta = res.meta || res.original?.meta || {};

      // ----------------------------
      //instagram (adjustedValues)
      // ----------------------------
      if (isInstagram && meta.adjustedValues?.length) {
        this.lastInstaMeta = meta;
        let adjustedValues: number[] = meta.adjustedValues;
        let dates: string[] = meta.dates || [];

        if (this.selectedPeriod === "week" && adjustedValues.length > 7) {
          const startIndex = adjustedValues.length - 7;
          adjustedValues = adjustedValues.slice(startIndex);
          dates = dates.slice(startIndex);
        }

        const categories =
          dates.length === adjustedValues.length
            ? dates.map((d) => {
              const date = new Date(d.replace("+0000", "Z"));
              date.setDate(date.getDate() + 1);
              return date.toLocaleDateString("el-GR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric"
              });
            })
            : adjustedValues.map((_, i) => `Day ${i + 1}`);

        this.chartOptions6 = this.createChartOptions(
          adjustedValues,
          categories
        );

        const lastValue = adjustedValues.at(-1) ?? 0;
        const firstValue = adjustedValues[0] ?? 0;
        this.lastChartValue = Number(meta.todayFollowers || lastValue);
        this.diffChartValue = lastValue - firstValue;

        this.cdr.detectChanges();
        return;
      }

      // ----------------------------
      // facebook (insights flow)
      // ----------------------------
      const data = res.original?.data || res.data || [];
      if (!data.length) {
        this.chartOptions6 = this.createChartOptions([], []);
        this.cdr.detectChanges();
        return;
      }

      const values = data[0]?.insight?.data?.[0]?.values || [];
      if (!values.length) {
        this.chartOptions6 = this.createChartOptions([], []);
        this.cdr.detectChanges();
        return;
      }

      const chartData = values.map((v: any) => ({
        end_time: v.end_time,
        value: v.value,
      }));

      this.chartOptions6 = this.prepareChart(chartData);

      const lastValue = chartData.at(-1)?.value ?? 0;
      const firstValue = chartData[0]?.value ?? 0;
      this.lastChartValue = lastValue;
      this.diffChartValue = lastValue - firstValue;

      this.cdr.detectChanges();
    });
  }

  private fetchFollowersOnlyForMetricCards(): Promise<MetricValue> {
    return new Promise((resolve) => {
      const platform = this.filteredPlatforms.find(p => p.id === this.selectedPlatformId);
      if (!platform) return resolve({ value: null, diff: null });

      // Don't fetch data for placeholder TikTok
      const isPlaceholderTikTok = platform.id === -999;
      if (isPlaceholderTikTok) {
        return resolve({ value: null, diff: null });
      }

      const isIG = platform.platform.toLowerCase().includes("instagram");

      const periodToUse = isIG ? "day" : this.selectedPeriod;

      const endpoint = isIG
        ? `querymetrics/parseInstaAnalytics/${this.companyID}?metric=follower_count`
        : `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_follows`;

      const payload = {
        content_site_id: this.selectedSiteId,
        social_platform_id: this.selectedPlatformId,
        period: periodToUse,
        pages: this.pages
      };

      this.coreService.postData(endpoint, payload).subscribe((res: any) => {
        if (isIG) {
          const meta = res.meta || res.original?.meta || {};
          const arr = meta.adjustedValues || [];
          if (!arr.length) return resolve({ value: null, diff: null });

          const first = arr[0];
          const last = arr[arr.length - 1];

          return resolve({
            value: Number(meta.todayFollowers ?? last),
            diff: last - first
          });
        }

        // Facebook
        const values = res.data?.[0]?.insight?.data?.[0]?.values || [];
        if (!values.length) return resolve({ value: null, diff: null });

        const first = values[0].value;
        const last = values[values.length - 1].value;

        resolve({ value: last, diff: last - first });
      });
    });
  }

  private getInstagramMetricPeriodForCards(): string {
    if (this.selectedPeriod === 'days_28' || this.selectedPeriod === '30d') {
      return 'days_28';
    }

    if (this.selectedPeriod === 'week' || this.selectedPeriod === '7d') {
      return 'week';
    }

    return 'day';
  }

  tableAlertTypes = [
    { value: null, label: 'All Types' },
    { value: 'Low Performance', label: 'Low Performance' },
    { value: 'Spike in Saves', label: 'Spike in Saves' },
    { value: 'Spike in Clicks', label: 'Spike in Clicks' },
    { value: 'Low Clicks', label: 'Low Clicks' },
  ];

  pageAlertTypes = [
    { value: null, label: 'All Types' },
    { value: 'success', label: 'Achievement' },
    { value: 'info', label: 'Increase' },
    { value: 'warning', label: 'Warning' },
    { value: 'danger', label: 'Danger' }
  ];

  /** ===============================
   *  CHART HELPERS
   *  =============================== */
  private prepareChart(data: any[]): ChartOptions {
    if (!data?.length) return this.createChartOptions([], []);

    if (this.selectedPeriod === "week" && data.length > 7) {
      data = data.slice(-7);
    }

    const categories = data.map((d) =>
      new Date(d.end_time).toLocaleDateString("el-GR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    );

    const values = data.map((d) => d.value);
    return this.createChartOptions(values, categories);
  }

  private createChartOptions(
    data: number[],
    categories: (string | number)[]
  ): ChartOptions {
    return {
      series: [{ name: "Followers", data }],
      chart: {
        type: "area",
        height: 360,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { curve: "smooth", width: 2, colors: ["#6ab7ff"] },
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#f1f3f4",
        strokeDashArray: 0,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
      yaxis: {
        tickAmount: 5,
        labels: {
          style: { colors: "#777", fontSize: "12px" },
          formatter: (val: number, index: number) =>
            index === 0 ? "" : val.toLocaleString(),
        },
      },
      xaxis: {
        categories,
        tickAmount: 6,
        labels: { style: { colors: "#777", fontSize: "12px" } },
      },
      tooltip: {
        theme: "light",
        y: { formatter: (val: number) => val.toLocaleString("el-GR") },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: "center",
        labels: { colors: "#888" },
      },
      title: {
        align: "left",
        style: {
          fontSize: "15px",
          fontWeight: "600",
          color: "#333",
        },
      },
    };
  }

  /** ===============================
   *  API HELPERS
   *  =============================== */
  loadSites(): any {
    return this.coreService.getData(`/sites-with-feeds/${this.companyID}`).pipe(
      take(1),
      map((res: any) => (res.data || []).filter((site: any) => site.hasFeed)),
      catchError(() => of([]))
    );
  }

  loadPlatforms(): any {
    return this.coreService.getData("/getSocialPlatforms").pipe(
      take(1),
      map((res: any) =>
        (res.data as SocialPlatformI[]).filter(
          // (p) => p.isApproved === "Approved" && !!p.access_token && p.platform !== 'Instagram'
          (p) => p.isApproved === "Approved" && !!p.access_token
        )
      ),
      catchError(() => of([]))
    );
  }

  loadMetrics(): void {
    if (!this.selectedSiteId || !this.selectedPlatformId) return;
    this.isMetricsLoaded = false;

    const selectedPlatform = this.filteredPlatforms.find(
      (p) => p.id === this.selectedPlatformId
    );

    // Don't load metrics for placeholder TikTok
    const isPlaceholderTikTok = selectedPlatform?.id === -999;
    if (isPlaceholderTikTok) {
      this.isMetricsLoaded = true;
      this.metrics = {
        reach: { value: null, diff: null },
        accountImpressions: { value: null, diff: null },
        profileImpressions: { value: null, diff: null },
      };
      this.cdr.detectChanges();
      return;
    }

    const isInstagram = selectedPlatform?.platform
      ?.toLowerCase()
      .includes("instagram");

    const periodToUse = isInstagram
      ? this.getInstagramMetricPeriodForCards()
      : this.selectedPeriod;

    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: periodToUse,
      pages: this.pages,
    };

    /* ============================================================
     * INSTAGRAM
     * ============================================================ */
    // NOTE: IG & FB have different response schemas.
    if (isInstagram) {
      const reach$ = this.coreService.postData(
        `querymetrics/parseInstaAnalytics/${this.companyID}?metric=reach`,
        payload
      );

      const accountImpressions$ = this.coreService.postData(
        `querymetrics/parseInstaAnalytics/${this.companyID}?metric=views`,
        payload
      );

      const profileImpressions$ = this.coreService.postData(
        `querymetrics/parseInstaAnalytics/${this.companyID}?metric=profile_views`,
        payload
      );

      forkJoin({
        reach: reach$,
        accountImpressions: accountImpressions$,
        profileImpressions: profileImpressions$,
      }).subscribe(({ reach, accountImpressions, profileImpressions }: any) => {

        /* -------- Reach -------- */
        const reachMeta = reach.meta || reach.original?.meta || {};
        if (reachMeta.dailyValues?.length) {
          const first = reachMeta.dailyValues[0];
          const last = reachMeta.dailyValues.at(-1);
          this.metrics.reach = {
            value: last,
            diff: last - first,
          };
        } else {
          this.metrics.reach = { value: null, diff: null };
        }

        /* -------- Account Impressions -------- */
        const accMeta =
          accountImpressions.meta || accountImpressions.original?.meta || {};
        if (accMeta.dailyValues?.length) {
          const first = accMeta.dailyValues[0];
          const last = accMeta.dailyValues.at(-1);
          this.metrics.accountImpressions = {
            value: last,
            diff: last - first,
          };
        } else {
          this.metrics.accountImpressions = { value: null, diff: null };
        }

        /* -------- Profile Impressions -------- */
        const profMeta =
          profileImpressions.meta || profileImpressions.original?.meta || {};
        if (profMeta.dailyValues?.length) {
          const first = profMeta.dailyValues[0];
          const last = profMeta.dailyValues.at(-1);
          this.metrics.profileImpressions = {
            value: last,
            diff: last - first,
          };
        } else {
          this.metrics.profileImpressions = { value: null, diff: null };
        }

        this.isMetricsLoaded = true;
        this.cdr.detectChanges();
      });

      return;
    }

    /* ============================================================
     * FACEBOOK
     * ============================================================ */
    const reach$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_total_media_view_unique`,
      payload
    );

    const accountImpressions$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_media_view`,
      payload
    );

    const profileImpressions$ = this.coreService.postData(
      `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_views_total`,
      payload
    );

    forkJoin({
      reach: reach$,
      accountImpressions: accountImpressions$,
      profileImpressions: profileImpressions$,
    }).subscribe(({ reach, accountImpressions, profileImpressions }) => {
      this.metrics.reach = this.extractMetric(reach);
      this.metrics.accountImpressions = this.extractMetric(accountImpressions);
      this.metrics.profileImpressions = this.extractMetric(profileImpressions);

      this.isMetricsLoaded = true;
      this.cdr.detectChanges();
    });
  }

}
